import { useHistory } from "react-router";

import {
    BdsButtonGroup,
    BdsIconCatalog,
    BdsIconListBullets,
    BdsRollupButton,
} from "@buildertrend/components";

import { routes } from "utilities/routes";

export type ViewMode = "list" | "book";

interface ISpecificationBookListToggleProps {
    viewMode: ViewMode;
}

/**
 * Icon-based toggle component to switch between Book View and List View for Specifications
 * Uses routing to navigate between views. Only rendered for builders.
 */
export function SpecificationBookListToggle({
    viewMode: currentViewMode,
}: Readonly<ISpecificationBookListToggleProps>) {
    const history = useHistory();

    const handleViewChange = (newView: string) => {
        if (newView === "book") {
            // Navigate to book view without ID (will auto-select first spec)
            history.push(routes.specification.getBookLink());
        } else {
            // Navigate to list view
            history.push(routes.specification.getListLink());
        }
    };

    // Create unique IDs based on current view mode to prevent duplicates during route transitions
    const idSuffix = currentViewMode === "book" ? "Book" : "List";

    return (
        <BdsButtonGroup
            id={`specificationViewToggle${idSuffix}`}
            testid="specification-view-toggle"
            compact
            stages={1}
            menuTestid={`specificationViewToggle${idSuffix}-menu`}
            rollupTestid={`specificationViewToggle${idSuffix}Rollup`}
            selectable
            flexAlign="end"
        >
            <BdsRollupButton
                id={`bookViewSelectButton${idSuffix}`}
                testid="view-toggle-book"
                onClick={() => handleViewChange("book")}
                selected={currentViewMode === "book"}
                icon={<BdsIconCatalog />}
                ariaLabel="Book view"
                value="book"
                minStage={1}
            />
            <BdsRollupButton
                id={`listViewSelectButton${idSuffix}`}
                testid="view-toggle-list"
                onClick={() => handleViewChange("list")}
                selected={currentViewMode === "list"}
                icon={<BdsIconListBullets />}
                ariaLabel="List view"
                value="list"
                minStage={1}
            />
        </BdsButtonGroup>
    );
}
